import React, { useState, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth } from '../utils/api';
import { formatCurrency } from '../utils/AuthUtils';
import { calculateLine, resolveVatRate } from '../utils/vat';
import { branchVatRate } from '../utils/vatConfig';
import Pagination from './Pagination';
import { Stethoscope, Plus, Edit, Trash2, X, Search } from 'lucide-react';

const Treatments = ({ currentBranch, user }) => {
  const { print_lang, currentLanguage } = useLanguage();

  // A treatment with no rate override inherits the branch rate; a stored 0 does not.
  const branchRate = branchVatRate(currentBranch);

  // How this treatment's price should be read at the till.
  const vatLabel = (mode) => ({
    include: print_lang('vat_include', 'Price includes VAT'),
    exclude: print_lang('vat_exclude', 'Price excludes VAT'),
    none: print_lang('vat_none', 'No VAT'),
  }[mode] || print_lang('vat_none', 'No VAT'));

  // What the patient actually pays for one unit, under this row's VAT mode.
  // Range pricing has no single figure to show, so callers only use this for
  // fixed prices.
  const priceInclVat = (row) => calculateLine({
    quantity: 1,
    unit_price: row.price === '' || row.price === undefined ? 0 : row.price,
    vat_mode: row.vat_mode,
    vat_rate: resolveVatRate({
      vatMode: row.vat_mode, itemRate: row.vat_rate, branchRate,
    }),
  }).total;

  // Helper function to get category display name based on language
  const getCategoryName = (cat) => {
    if (typeof cat === 'string') {
      // If cat is a string (from treatment.category), find the category object
      const categoryObj = categories.find(c => c.name === cat);
      if (categoryObj) {
        return currentLanguage === 'th' ? (categoryObj.name_th || categoryObj.name) : categoryObj.name;
      }
      return cat;
    }
    // If cat is an object
    return currentLanguage === 'th' ? (cat.name_th || cat.name) : cat.name;
  };
  const [treatments, setTreatments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [formData, setFormData] = useState({});

  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [treatmentsData, categoriesData] = await Promise.all([
        fetchWithAuth('/api/treatments'),
        fetchWithAuth('/api/treatments/categories')
      ]);
      setTreatments(treatmentsData || []);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Failed to fetch treatments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedTreatment(null);
    setFormData({
      code: '',
      name: '',
      name_en: '',
      category: categories[0]?.name || '',
      duration: 30,
      price: 0,
      price_type: 'fixed',
      price_min: null,
      price_max: null,
      vat_mode: 'none',
      vat_rate: '',
      df_percentage: null,
      description: ''
    });
    setShowModal(true);
  };

  const handleEdit = (treatment) => {
    setSelectedTreatment(treatment);
    setFormData({
      code: treatment.code || '',
      name: treatment.name || '',
      name_en: treatment.name_en || '',
      category: treatment.category || '',
      duration: treatment.duration || 30,
      price: treatment.price || 0,
      price_type: treatment.price_type || 'fixed',
      price_min: treatment.price_min,
      price_max: treatment.price_max,
      vat_mode: treatment.vat_mode || 'none',
      // '' means "no override, use the branch rate". A stored 0 is a real 0%
      // and must survive the round trip, so `??` rather than `||`.
      vat_rate: treatment.vat_rate ?? '',
      df_percentage: treatment.df_percentage,
      description: treatment.description || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTreatment) {
        await fetchWithAuth(`/api/treatments/${selectedTreatment.t_id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
      } else {
        await fetchWithAuth('/api/treatments', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save treatment:', error);
      alert(
        error.code === 'VAT_RATE_INVALID'
          ? print_lang('vat_rate_invalid', 'VAT rate must be a number between 0 and 100')
          : error.message
      );
    }
  };

  const handleDelete = async (tId) => {
    if (!window.confirm(print_lang('confirm_delete', 'Are you sure?'))) return;

    try {
      await fetchWithAuth(`/api/treatments/${tId}`, { method: 'DELETE' });
      fetchData();
    } catch (error) {
      console.error('Failed to delete treatment:', error);
      alert(error.message);
    }
  };

  // Filter treatments based on search and category
  const filteredTreatments = treatments.filter(treatment => {
    const matchesSearch = !searchTerm ||
      treatment.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      treatment.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      treatment.code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || treatment.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const totalCount = filteredTreatments.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedTreatments = filteredTreatments.slice(startIndex, startIndex + pageSize);

  // Group paginated treatments by category
  const groupedTreatments = paginatedTreatments.reduce((acc, treatment) => {
    const cat = treatment.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(treatment);
    return acc;
  }, {});

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Stethoscope size={28} />
          {print_lang('treatments', 'Treatments')}
        </h1>

        <button onClick={handleAdd} className="btn btn-primary flex items-center gap-2">
          <Plus size={18} />
          {print_lang('add', 'Add')} {print_lang('treatment', 'Treatment')}
        </button>
      </div>

      {/* Search and Filter */}
      <div className="card mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={print_lang('search_treatment', 'Search by name or code...')}
              className="pl-10"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={handleCategoryChange}
            className="w-48"
          >
            <option value="">{print_lang('all_categories', 'All Categories')}</option>
            {categories.map((cat) => (
              <option key={cat.category_id} value={cat.name}>{getCategoryName(cat)}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {Object.keys(groupedTreatments).length === 0 ? (
            <div className="card text-center py-12 text-gray-500">
              <Stethoscope size={48} className="mx-auto mb-4 text-gray-300" />
              <p>{print_lang('no_treatments_found', 'No treatments found')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedTreatments).map(([category, items]) => (
            <div key={category} className="card">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">{getCategoryName(category)}</h2>
              <table>
                <thead>
                  <tr>
                    <th>{print_lang('code', 'Code')}</th>
                    <th>{print_lang('name', 'Name')}</th>
                    <th>{print_lang('duration', 'Duration')}</th>
                    <th>{print_lang('price', 'Price')}</th>
                    <th>{print_lang('doctor_fee', 'DF %')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((treatment) => (
                    <tr key={treatment.t_id}>
                      <td className="font-mono text-sm">{treatment.code || '-'}</td>
                      <td>
                        <p className="font-medium">{treatment.name}</p>
                        {treatment.name_en && (
                          <p className="text-sm text-gray-500">{treatment.name_en}</p>
                        )}
                      </td>
                      <td>{treatment.duration} min</td>
                      <td>
                        {treatment.price_type === 'range' ? (
                          <span>{formatCurrency(treatment.price_min)} - {formatCurrency(treatment.price_max)}</span>
                        ) : (
                          formatCurrency(treatment.price)
                        )}
                        {/* Only VAT-liable rows say anything: a price with no
                            note beneath it means no VAT, which is what every
                            row means on a clinic that does not charge it. */}
                        {treatment.vat_mode && treatment.vat_mode !== 'none' && (
                          <p className="text-xs text-gray-500">
                            {vatLabel(treatment.vat_mode)} ({resolveVatRate({
                              vatMode: treatment.vat_mode,
                              itemRate: treatment.vat_rate,
                              branchRate,
                            })}%)
                            {treatment.vat_mode === 'exclude' && treatment.price_type !== 'range' && (
                              <> · {formatCurrency(priceInclVat(treatment))}</>
                            )}
                          </p>
                        )}
                      </td>
                      <td>{treatment.df_percentage ? `${treatment.df_percentage}%` : '-'}</td>
                      <td>
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => handleEdit(treatment)} className="p-2 hover:bg-gray-100 rounded">
                            <Edit size={16} className="text-blue-600" />
                          </button>
                          <button onClick={() => handleDelete(treatment.t_id)} className="p-2 hover:bg-gray-100 rounded">
                            <Trash2 size={16} className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="card">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                print_lang={print_lang}
              />
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {selectedTreatment ? print_lang('edit', 'Edit') : print_lang('add', 'Add')} {print_lang('treatment', 'Treatment')}
              </h2>
              <button onClick={() => setShowModal(false)}><X size={24} /></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('code', 'Code')}</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('category', 'Category')} *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                    >
                      {categories.map((cat) => (
                        <option key={cat.category_id} value={cat.name}>{getCategoryName(cat)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('name', 'Name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('duration', 'Duration')} (min)</label>
                    <input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('price_type', 'Price Type')}</label>
                    <select
                      value={formData.price_type}
                      onChange={(e) => setFormData({ ...formData, price_type: e.target.value })}
                    >
                      <option value="fixed">{print_lang('fixed', 'Fixed')}</option>
                      <option value="range">{print_lang('range', 'Range')}</option>
                    </select>
                  </div>
                </div>

                {formData.price_type === 'fixed' ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('price', 'Price')}</label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      step="0.01"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('min_price', 'Min Price')}</label>
                      <input
                        type="number"
                        value={formData.price_min || ''}
                        onChange={(e) => setFormData({ ...formData, price_min: e.target.value })}
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('max_price', 'Max Price')}</label>
                      <input
                        type="number"
                        value={formData.price_max || ''}
                        onChange={(e) => setFormData({ ...formData, price_max: e.target.value })}
                        step="0.01"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('vat_mode', 'VAT')}</label>
                    <select
                      value={formData.vat_mode || 'none'}
                      onChange={(e) => setFormData({ ...formData, vat_mode: e.target.value })}
                    >
                      <option value="none">{print_lang('vat_none', 'No VAT')}</option>
                      <option value="include">{print_lang('vat_include', 'Price includes VAT')}</option>
                      <option value="exclude">{print_lang('vat_exclude', 'Price excludes VAT')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('vat_rate_override', 'VAT Rate Override')}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder={`${print_lang('use_branch_rate', 'Use branch rate')} (${branchRate}%)`}
                      disabled={(formData.vat_mode || 'none') === 'none'}
                      value={formData.vat_rate ?? ''}
                      onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('price_incl_vat', 'Price incl. VAT')}
                    </label>
                    {/* A range has no single price to gross up; show the mode's
                        effect on the fixed price only. */}
                    <div className="px-3 py-2 font-medium">
                      {formData.price_type === 'range'
                        ? '-'
                        : priceInclVat(formData).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('doctor_fee', 'Doctor Fee %')}</label>
                  <input
                    type="number"
                    value={formData.df_percentage || ''}
                    onChange={(e) => setFormData({ ...formData, df_percentage: e.target.value })}
                    step="0.01"
                    placeholder="Leave empty to use staff default"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('description', 'Description')}</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {print_lang('save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Treatments;
